import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useModal } from "@/hooks/useModal";
import { BuyerTypeEnum, PaymentMethodEnum } from "@/enum";
import { ExpensesType } from "@/interfaces/expense";
import { createFileRoute } from "@tanstack/react-router";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import Select from "../../../../components/common/Select";
import Label from "@/components/form/Label";
import "react-toastify/dist/ReactToastify.css";
import { expenseTypeList } from "@/constants/expense";
import { MainContainer } from "@/components/ui/MainContainer";
import { toast, ToastContainer } from "react-toastify";
import BasicTableOne from "@/components/tables/BasicTables/BasicTableOne";
import { useSelector } from "react-redux";
import { SellerBuyerRootMapping } from "@/interfaces";
import { useExpenseOrderMutation, useGetOrderListQuery } from "@/features/order";
import { currentUserSelector } from "@/features/auth";
import { decimal } from "@/utils";

export const Route = createFileRoute("/(app)/(store)/expense/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { isOpen, openModal, closeModal } = useModal();
  const [selectedExpense, setSelectedExpense] = useState<
    ExpensesType | undefined
  >(undefined);
  const [createExpenseEntry] = useExpenseOrderMutation();
  const user = useSelector(currentUserSelector);
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data: orderList, isFetching } = useGetOrderListQuery({
    page,
    limit,
    "buyer-store": user?._id,
    "order-type": "BuyOrder",
  });

  const transformedOrderList: SellerBuyerRootMapping[] = (orderList || []).map(order => ({
    _id: order._id,
    status: order.status === "Active" ? "Active" : "Inactive", // Ensure correct type
    buyerType: order.buyerType as BuyerTypeEnum, // Ensure correct enum type
    buyerStoreId: order.buyerStoreId,
    totalDueAmount: 0, // Default value (Update if available)
    sellerType: order.sellerStore ? (order.sellerStore._id as BuyerTypeEnum) : "Unknown" as BuyerTypeEnum, // Ensure sellerType matches BuyerTypeEnum
  }));

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    setPage(newPage);
  };

  const { control, handleSubmit, reset } = useForm();

  const handleSelectExpense = (selected: string) => {
    const expenseData = JSON.parse(selected) as ExpensesType;
    setSelectedExpense(expenseData);
  };

  const submitExpense = async (params: any) => {
    try {
      const {
        type,
        name,
        description,
        category,
        amount,
        taxPercentage,
        taxAmount,
        render,
        ...rest
      } = { ...selectedExpense, ...params };
  
      // Define the data type
      const data: {
        sellerType: string;
        serviceList: {
          type: any;
          name: any;
          description: any;
          category: any;
          price: number;
          taxPercentage: number;
          taxAmount: any;
          meta: any;
        }[];
        paymentMethod: PaymentMethodEnum;
        totalAmount: number;
        paidAmount: number;
      } = {
        sellerType: "Store",
        serviceList: [
          {
            type,
            name,
            description,
            category,
            price: +amount,
            taxPercentage: +taxPercentage,
            taxAmount,
            meta: rest,
          },
        ],
        paymentMethod: PaymentMethodEnum.Cash,
        totalAmount: 0, // Initialize totalAmount
        paidAmount: 0, // Initialize paidAmount
      };
  
      // Calculate totalAmount
      data.totalAmount = data.serviceList.reduce(
        (acc, next) => acc + next.price + decimal(next.price).mul(next.taxPercentage).div(100).toNumber(),
        0
      );
  
      data.paidAmount = data.totalAmount;
  
      // Send API request
      const response = await createExpenseEntry(data).unwrap();
      if (response) {
        setSelectedExpense(undefined);
        toast.success("Expense added successfully");
        reset();
        closeModal();
      }
    } catch (error: any) {
      console.error("Expense Submission Error:", error);
    }
  };

  return (
    <MainContainer className="flex flex-col px-4">
      <ToastContainer style={{ marginTop: "80px" }} />

      {/* Expense Table with Orders */}
      <div className="w-full px-2 sm:px-4">
        <BasicTableOne
          data={transformedOrderList}
          isLoading={isFetching}
          currentPage={page}
          limit={limit}
          onPageChange={handlePageChange}
          screen={'Expense'}
        />
      </div>

      {/* Add Expense Button */}
      <Button
        onClick={openModal}
        className="fixed bottom-6 right-6 bg-themesText-600 text-surfaceBase px-5 py-3 rounded-full shadow-lg hover:bg-themesText-700 transition"
      >
        Add Expense
      </Button>

      {/* Expense Modal */}
      <Modal isOpen={isOpen} onClose={closeModal} className="max-w-[700px] m-4">
        <div className="relative w-full p-6 bg-surfaceBase rounded-3xl shadow-lg">
          <h4 className="text-2xl font-semibold text-gray-800">
            {selectedExpense ? selectedExpense.name : "Add Expense"}
          </h4>
          <p className="text-sm text-gray-500 mb-4">
            {selectedExpense
              ? "Review and update expense details."
              : "Select an expense type to proceed."}
          </p>

          <Select
            options={expenseTypeList.map((expense) => ({
              label: expense.name,
              value: JSON.stringify(expense),
            }))}
            onChange={handleSelectExpense}
            value={selectedExpense ? JSON.stringify(selectedExpense) : ""}
          />

          {/* Expense Details Form */}
          {selectedExpense && (
            <form
              onSubmit={handleSubmit(submitExpense)}
              className="space-y-4 mt-4"
            >
              <div className="grid grid-cols-1 gap-x-6 gap-y-5 lg:grid-cols-2">
                {selectedExpense.render.map((field) => (
                  <div key={field.key}>
                    <Label>{field.label}</Label>
                    <Controller
                      control={control}
                      name={field.key}
                      render={({ field: { onChange, value } }) => (
                        <div>
                          <input
                            className="h-10 rounded-lg border border-gray-300 mx-0.5 px-3 w-full max-w-full"
                            placeholder={
                              field.placeholder +
                              (field.type === "date" ? " DD-MM-YYYY" : "")
                            }
                            value={value}
                            onChange={onChange}
                          />
                        </div>
                      )}
                    />
                    {/* {errors.serviceList?.[0]?.[
                      field.key as keyof ExpensesType
                    ] && (
                      <p className="text-themesText4-500 text-sm">
                        {errors.serviceList?.[0]?.[
                          field.key as keyof ExpensesType
                        ]?.toString()}
                      </p>
                    )} */}
                  </div>
                ))}
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 mt-4">
                <Button
                  variant="outline"
                  onClick={closeModal}
                  className="px-5 py-2 text-gray-700 border border-gray-400 rounded-lg hover:bg-gray-200"
                >
                  Close
                </Button>

                <Button
                  variant="outline"
                  onClick={() => setSelectedExpense(undefined)}
                  className="px-5 py-2 text-themesText-800 border border-themesText-800 rounded-lg hover:bg-themesText-100"
                >
                  Reset
                </Button>

                <Button
                  type="submit"
                  className="px-6 py-2 text-surfaceBase bg-themesText-800 rounded-lg shadow-md hover:bg-themesText-700"
                >
                  Submit
                </Button>
              </div>
            </form>
          )}
        </div>
      </Modal>
    </MainContainer>
  );
}
